import { useState, useRef, useEffect, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { PiHandArrowDown } from "react-icons/pi";
import { FaRegFolderOpen } from "react-icons/fa6";
import { RiDeleteBin2Line } from "react-icons/ri";
import { FaWpforms } from "react-icons/fa";
import { LuMailX } from "react-icons/lu";
import { TbShieldCheck, TbClipboardCheck, TbClipboardList, TbClipboardText, TbChevronDown, TbQrcode, TbRefresh } from "react-icons/tb";
import { getAvatarColor, getTagStyle } from "src/helpers/randomColor";
import { normalizePhone } from "src/helpers/sanitizeInput";
import { isRecipientSameAsPatient } from "src/helpers/recipientSamePerson";
import { formatSalutationLabel } from "src/helpers/salutation";
import { isRecipientPostOnly } from "src/utils/postOnly";
import { Recipient, Group } from "src/types";

function age(birthDate?: string): number {
    if (!birthDate) return 0;
    const birth = new Date(birthDate);
    const today = new Date();
    let a = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) a--;
    return a;
}

function genderShort(gender?: string): string {
    if (!gender) return "";
    const g = gender.toUpperCase();
    if (g === "MALE"   || g === "M") return "M";
    if (g === "FEMALE" || g === "F" || g === "W") return "F";
    if (g === "DIVERSE" || g === "D") return "D";
    return g.charAt(0);
}

function deriveGender(gender?: string, salutation?: string): string {
    if (gender) return gender;
    if (salutation === "F") return "W";
    if (salutation === "H") return "M";
    return "";
}

function patientSubLine(title?: string, gender?: string, birthDate?: string): string {
    const parts = [
        title ?? "",
        genderShort(gender),
        birthDate ? `${new Date(birthDate).getFullYear()} (${age(birthDate)} y)` : "",
    ].filter(Boolean);
    return parts.join(" · ");
}

function RecipientContactMeta({ recipient, navigate }: { recipient: Recipient; navigate: (path: string) => void }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const cardRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [cardCoords, setCardCoords] = useState<{ top: number; left: number } | null>(null);

    const hasAddress = !!(recipient.street || recipient.plz || recipient.city);
    const hasEmail   = !!recipient.email;
    const hasTel     = (recipient.tel ?? []).length > 0;

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (
                ref.current && !ref.current.contains(e.target as Node) &&
                cardRef.current && !cardRef.current.contains(e.target as Node)
            ) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const handleToggle = () => {
        if (!open && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const cardWidth = 280;
            const left = Math.min(
                Math.max(8, rect.right - cardWidth),   // align right edge to trigger, don't go past left
                window.innerWidth - cardWidth - 8,      // don't go past right
            );
            setCardCoords({ top: rect.bottom + 6, left });
        }
        setOpen((o) => !o);
    };

    const address = [
        recipient.street,
        [recipient.plz, recipient.city].filter(Boolean).join(" "),
    ].filter(Boolean).join(", ");
    const tel = (recipient.tel ?? []).map(normalizePhone).join(", ");

    const goToRecipient = () => navigate(`/patients/${recipient.id}`);

    return (
        <div className="info-cell" ref={ref} onClick={(e) => e.stopPropagation()}>
            <button
                type="button"
                ref={triggerRef}
                className="info-cell__trigger"
                onClick={handleToggle}
            >
                <span className={`info-cell__tag${hasAddress ? " info-cell__tag--on" : " info-cell__tag--off"}`}>
                    {t("form.address")}
                </span>
                <span className="info-cell__sep">|</span>
                <span className={`info-cell__tag${hasEmail ? " info-cell__tag--on" : " info-cell__tag--off"}`}>
                    {t("table.email")}
                </span>
                <span className="info-cell__sep">|</span>
                <span className={`info-cell__tag${hasTel ? " info-cell__tag--on" : " info-cell__tag--off"}`}>
                    {t("form.tel")}
                </span>
                <TbChevronDown className={`info-cell__chevron${open ? " info-cell__chevron--open" : ""}`} />
            </button>
            {open && cardCoords && createPortal(
                <div
                    ref={cardRef}
                    className="info-cell__card"
                    style={{ top: cardCoords.top, left: cardCoords.left } as React.CSSProperties}
                >
                    <div className="info-cell__row">
                        <span className="info-cell__row-label">{t("form.address")}</span>
                        {hasAddress
                            ? <button type="button" className="info-cell__row-link" onClick={goToRecipient}>{address}</button>
                            : <button type="button" className="info-cell__row-link info-cell__row-link--missing" onClick={goToRecipient}>{t("recipients.noAddressShort")}</button>
                        }
                    </div>
                    <div className="info-cell__row">
                        <span className="info-cell__row-label">{t("table.email")}</span>
                        {hasEmail
                            ? <button type="button" className="info-cell__row-link" onClick={goToRecipient}>{recipient.email}</button>
                            : <button type="button" className="info-cell__row-link info-cell__row-link--missing" onClick={goToRecipient}>{t("recipients.noEmailShort")}</button>
                        }
                    </div>
                    <div className="info-cell__row">
                        <span className="info-cell__row-label">{t("form.tel")}</span>
                        <span className="info-cell__row-value">{tel || "—"}</span>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

type PatientRow = { recipientId: string; patientId: string | null; firstName?: string; lastName?: string };

interface RecipientRowProps {
    recipient: Recipient;
    navigate: (path: string, options?: object) => void;
    onDeleteClick?: (recipient: Recipient) => void;
    onPatientDeleteClick?: (patient: any, recipient: Recipient) => void;
    onQuestionnaireClick: (recipient: Recipient) => void;
    onQrClick: (recipient: Recipient) => void;
    selectedIds: Set<string>;
    onToggle: (id: string) => void;
    canWrite?: boolean;
    orderedPatientRows?: PatientRow[];
    showGroupsUI?: boolean;
    groupMap?: Map<string, Group>;
    onManageGroups?: (patient: any) => void;
}

function GroupCell({ patient, groupMap, onManageGroups }: { patient: any; groupMap: Map<string, Group>; onManageGroups: (patient: any) => void }) {
    const groupIds: string[] = patient?.groupIds ?? [];
    return (
        <td className="patient-group-td" onClick={(e) => { e.stopPropagation(); if (patient) onManageGroups(patient); }}>
            {patient ? (
                groupIds.length ? (
                    groupIds.map((gid) => {
                        const group = groupMap.get(gid);
                        return group ? (
                            <span key={gid} className="patient-group-chip" style={getTagStyle(group.name) as CSSProperties}>
                                {group.name}
                            </span>
                        ) : null;
                    })
                ) : (
                    <span className="row-mini row-mini--dim">—</span>
                )
            ) : (
                <span className="row-mini row-mini--dim">—</span>
            )}
        </td>
    );
}

function RecipientActions({ recipient, navigate, onDeleteClick, onQuestionnaireClick, onQrClick, canWrite, t, patientId, orderedPatientRows }: {
    recipient: Recipient;
    navigate: (path: string, options?: object) => void;
    onDeleteClick?: (r: Recipient) => void;
    onQuestionnaireClick: (r: Recipient) => void;
    onQrClick: (r: Recipient) => void;
    canWrite: boolean;
    t: (key: string) => string;
    patientId?: string;
    orderedPatientRows?: PatientRow[];
}) {
    const postOnly = isRecipientPostOnly(recipient);
    return (
        <div className="row-actions">
            <button title={t("recipients.editRecipient")} onClick={() => navigate(`/patients/${recipient.id}`, { state: { ...(patientId ? { patientId, tab: "details" } : {}), ...(orderedPatientRows ? { patientRows: orderedPatientRows } : {}) } })}>
                <FaRegFolderOpen />
            </button>
            {canWrite && (
                <>
                    <button
                        className="success questionnaire-action-btn"
                        title={
                            postOnly ? t("recipientPage.postOnlyConsentHint")
                            : !recipient.email ? t("recipients.noEmail")
                            : recipient.questionnaireStatus === "submitted" ? t("recipients.questionnaireResend")
                            : recipient.questionnaireStatus === "sent" ? t("recipients.questionnaireResend")
                            : recipient.questionnaireStatus === "opened" ? t("recipients.questionnaireResend")
                            : t("recipients.sendQuestionnaire")
                        }
                        disabled={postOnly || !recipient.email}
                        onClick={() => onQuestionnaireClick(recipient)}
                    >
                        <FaWpforms />
                        {(recipient.questionnaireStatus === "submitted" ||
                            recipient.questionnaireStatus === "sent" ||
                            recipient.questionnaireStatus === "opened") && (
                            <TbRefresh className="questionnaire-action-btn__badge" />
                        )}
                    </button>
                    <button
                        className="purple"
                        title={postOnly ? t("recipientPage.postOnlyConsentHint") : t("recipientPage.questionnaireQr")}
                        disabled={postOnly}
                        onClick={() => onQrClick(recipient)}
                    >
                        <TbQrcode />
                    </button>
                    {onDeleteClick && (
                        <button className="danger" title={t("recipients.deleteTitle")} onClick={() => onDeleteClick(recipient)}>
                            <RiDeleteBin2Line />
                        </button>
                    )}
                </>
            )}
        </div>
    );
}

export function RecipientRow({ recipient, navigate, onDeleteClick, onPatientDeleteClick, onQuestionnaireClick, onQrClick, selectedIds, onToggle, canWrite = true, orderedPatientRows, showGroupsUI = false, groupMap, onManageGroups }: RecipientRowProps) {
    const { t } = useTranslation();
    const salutation = formatSalutationLabel(recipient.salutation, t);
    const samePerson = isRecipientSameAsPatient(recipient);
    const patientList = (recipient.patients ?? []).filter(Boolean);

    // Portal + questionnaire/consent status — shared by the payer cell and every "Selbst" badge,
    // so a self-paying patient shows the same icons as a separate payer would.
    const statusChips = (
        <>
            {recipient.hasPortalAccount && (
                <span className="chip chip--success" title={t("recipients.portalRegistered")}><TbShieldCheck /></span>
            )}
            {recipient.questionnaireStatus === "submitted" && (
                <span className="chip chip--success" title={t("recipients.questionnaireSubmitted")}><TbClipboardCheck /></span>
            )}
            {recipient.questionnaireStatus === "opened" && (
                <span className="chip chip--info" title={t("recipients.questionnaireOpened")}><TbClipboardText /></span>
            )}
            {recipient.questionnaireStatus === "sent" && (
                <span className="chip chip--warning" title={t("recipients.questionnaireSent")}><TbClipboardList /></span>
            )}
        </>
    );
    const selfPaysCell = (
        <>
            <span className="recipient-same-as-badge" title={t("invoicePage.samePersonBadge")}>
                {t("invoicePage.selfPays")}
            </span>
            {statusChips}
        </>
    );

    const recipientCell = (
        <span
            className="recipient-billing-row recipient-billing-row--link"
            title={t("invoices.linkedAccountHolder")}
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); navigate(`/patients/${recipient.id}`, { state: { tab: "recipient", ...(orderedPatientRows ? { patientRows: orderedPatientRows } : {}) } }); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); navigate(`/patients/${recipient.id}`, { state: { tab: "recipient", ...(orderedPatientRows ? { patientRows: orderedPatientRows } : {}) } }); } }}
        >
            <PiHandArrowDown className="recipient-billing-row__icon" />
            <span className="recipient-billing-row__name">
                {[salutation, recipient.title, recipient.firstName, recipient.lastName].filter(Boolean).join(" ")}
            </span>
            {statusChips}
        </span>
    );

    // Multiple patients: one independent row per patient, same recipient column on each
    if (patientList.length > 1) {
        return (
            <>
                {patientList.map((patient, idx) => {
                    const isFirst = idx === 0;
                    const avatarKey = `${patient.firstName}${patient.lastName}`;
                    const initial = (patient.firstName || patient.lastName)?.charAt(0).toUpperCase();
                    const patNotSent = patient.invoicesNotSent ?? 0;
                    const isSelfRow = !!recipient.sameAsPatientId && patient.id === recipient.sameAsPatientId;
                    return (
                        <tr
                            key={patient.id}
                            className={`clickable-row${selectedIds.has(patient.id) ? " row--selected" : ""}${isFirst ? " row--grouped-first" : " row--grouped-continuation"}`}
                            onClick={() => navigate(`/patients/${recipient.id}`, { state: { patientId: patient.id, tab: "details", ...(orderedPatientRows ? { patientRows: orderedPatientRows } : {}) } })}
                        >
                            <td className="row-check" onClick={(e) => e.stopPropagation()}>
                                {canWrite && <input type="checkbox" checked={selectedIds.has(patient.id)} onChange={() => onToggle(patient.id)} />}
                            </td>
                            <td className="name-cell">
                                <div className="avatar" style={{ backgroundColor: getAvatarColor(avatarKey) }}>
                                    {initial}
                                </div>
                                <div className="name-info">
                                    <div className="name-info__primary">
                                        <span className="full-name">{patient.firstName} {patient.lastName}</span>
                                    </div>
                                    {patientSubLine(patient.title, deriveGender(patient.gender, patient.salutation), patient.birthDate) && (
                                        <span className="name-info__sub">
                                            {patientSubLine(patient.title, deriveGender(patient.gender, patient.salutation), patient.birthDate)}
                                        </span>
                                    )}
                                    <div className="recipient-col-inline">
                                        {isSelfRow ? selfPaysCell : recipientCell}
                                    </div>
                                </div>
                            </td>
                            <td className="col-info recipients-table__info-col" onClick={(e) => e.stopPropagation()}>
                                <RecipientContactMeta recipient={recipient} navigate={navigate} />
                            </td>
                            <td>
                                {isSelfRow ? selfPaysCell : recipientCell}
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                                {patNotSent > 0 ? (
                                    <div
                                        className="link inv-stat inv-stat--notsent"
                                        title={(patient.invoicesNotSentIds ?? []).map((id: string) => `#${id}`).join(", ")}
                                        onClick={() => navigate("/invoices", { state: { filters: { sent: "unsent" }, search: `${patient.firstName} ${patient.lastName}` } })}
                                    >
                                        <LuMailX /> {patNotSent} {t("common.notSent")}
                                    </div>
                                ) : (patient.invoicesDone ?? 0) + (patient.invoicesOpen ?? 0) > 0 ? (
                                    <span className="inv-stat inv-stat--allsent">{t("common.allSent")}</span>
                                ) : "—"}
                            </td>
                            {showGroupsUI && groupMap && onManageGroups && (
                                <GroupCell patient={patient} groupMap={groupMap} onManageGroups={onManageGroups} />
                            )}
                            <td className="row-actions-cell" onClick={(e) => e.stopPropagation()}>
                                <RecipientActions recipient={recipient} navigate={navigate} onDeleteClick={onPatientDeleteClick ? () => onPatientDeleteClick(patient, recipient) : onDeleteClick} onQuestionnaireClick={onQuestionnaireClick} onQrClick={onQrClick} canWrite={canWrite} t={t} patientId={patient.id} orderedPatientRows={orderedPatientRows} />
                            </td>
                        </tr>
                    );
                })}
            </>
        );
    }

    // Single patient (or same-person): original single-row rendering
    const singlePatient    = !samePerson && patientList.length === 1 ? patientList[0] : null;
    // Groups belong to the Patient document — when the recipient IS the patient (samePerson),
    // resolve that same patient record from patientList instead of the recipient itself.
    const groupPatient = singlePatient ?? (samePerson
        ? patientList.find((p: any) => p.id === (recipient as any).sameAsPatientId) ?? null
        : null);
    const patientAvatarKey = singlePatient
        ? `${singlePatient.firstName}${singlePatient.lastName}`
        : `${recipient.firstName}${recipient.lastName}`;
    const patientInitial   = (
        singlePatient ? (singlePatient.firstName || singlePatient.lastName) : (recipient.firstName || recipient.lastName)
    )?.charAt(0).toUpperCase();

    return (
        <tr
            className={`clickable-row${selectedIds.has(recipient.id) ? " row--selected" : ""}${samePerson ? " row--same-person" : ""}`}
            onClick={() => navigate(`/patients/${recipient.id}`, { state: { ...(singlePatient ? { patientId: singlePatient.id, tab: "patients" } : {}), ...(orderedPatientRows ? { patientRows: orderedPatientRows } : {}) } })}
        >
            <td className="row-check" onClick={(e) => e.stopPropagation()}>
                {canWrite && <input type="checkbox" checked={selectedIds.has(recipient.id)} onChange={() => onToggle(recipient.id)} />}
            </td>
            <td className="name-cell">
                <div className="avatar" style={{ backgroundColor: getAvatarColor(patientAvatarKey) }}>
                    {patientInitial}
                </div>
                <div className="name-info">
                    <div className="name-info__primary">
                        <span className="full-name">
                            {singlePatient
                                ? `${singlePatient.firstName} ${singlePatient.lastName}`
                                : `${salutation} ${recipient.title} ${recipient.firstName} ${recipient.lastName}`.trim()}
                        </span>
                    </div>
                    {(() => {
                        // Pick every field from one person only — singlePatient is a genuinely
                        // different person from recipient here (samePerson is handled separately
                        // above), so mixing e.g. the patient's own birthDate with the recipient's
                        // title would misattribute the recipient's personal data to the patient.
                        const subLine = singlePatient
                            ? patientSubLine(singlePatient.title, deriveGender(singlePatient.gender, singlePatient.salutation), singlePatient.birthDate)
                            : patientSubLine(recipient.title, deriveGender(undefined, recipient.salutation), recipient.birthDate);
                        return subLine && <span className="name-info__sub">{subLine}</span>;
                    })()}
                    <div className="recipients-table__info-inline">
                        <RecipientContactMeta recipient={recipient} navigate={navigate} />
                    </div>
                    <div className="recipient-col-inline">
                        {samePerson ? selfPaysCell : recipientCell}
                    </div>
                </div>
            </td>
            <td className="col-info recipients-table__info-col" onClick={(e) => e.stopPropagation()}>
                <RecipientContactMeta recipient={recipient} navigate={navigate} />
            </td>
            <td>
                {samePerson ? selfPaysCell : recipientCell}
            </td>
            <td onClick={(e) => e.stopPropagation()}>
                {(recipient.invoicesNotSent ?? 0) > 0 ? (
                    <div
                        className="link inv-stat inv-stat--notsent"
                        title={(recipient.invoicesNotSentIds ?? []).map((id: string) => `#${id}`).join(", ")}
                        onClick={() => navigate("/invoices", { state: { filters: { sent: "unsent" }, search: `${recipient.firstName} ${recipient.lastName}` } })}
                    >
                        <LuMailX /> {recipient.invoicesNotSent} {t("common.notSent")}
                    </div>
                ) : (recipient.invoicesDone ?? 0) + (recipient.invoicesOpen ?? 0) > 0 ? (
                    <span className="inv-stat inv-stat--allsent">{t("common.allSent")}</span>
                ) : (
                    <span className="inv-stat inv-stat--nothing">{t("common.nothingToSend")}</span>
                )}
            </td>
            {showGroupsUI && groupMap && onManageGroups && (
                <GroupCell patient={groupPatient} groupMap={groupMap} onManageGroups={onManageGroups} />
            )}
            <td className="row-actions-cell" onClick={(e) => e.stopPropagation()}>
                <RecipientActions recipient={recipient} navigate={navigate} onDeleteClick={onDeleteClick} onQuestionnaireClick={onQuestionnaireClick} onQrClick={onQrClick} canWrite={canWrite} t={t} orderedPatientRows={orderedPatientRows} />
            </td>
        </tr>
    );
}
